/**
 * Ported from Panel CreateSegmentModal - used for read-only conflict highlighting in Admin.
 * Does not block any actions; Admin is read-only.
 */

import i18n from '@/i18n/config';

export interface RuleDraft {
  id: string;
  propertyKey: string;
  operator: string;
  value: string[];
}

export interface SegmentValidationError {
  type: 'direct' | 'mutually_exclusive';
  message: string;
  ruleIds: string[];
}

export interface SegmentValidationResult {
  valid: boolean;
  errors: SegmentValidationError[];
}

function normalizeValue(v: string): string {
  return String(v ?? '').trim().toLowerCase();
}

function arraysOverlap(a: string[], b: string[]): boolean {
  const setA = new Set(a.map(normalizeValue).filter(Boolean));
  return b.some((v) => setA.has(normalizeValue(v)));
}

function arraysDisjoint(a: string[], b: string[]): boolean {
  return !arraysOverlap(a, b);
}

export function validateSegmentRules(
  rules: RuleDraft[],
  propertyMap?: Map<string, string>
): SegmentValidationResult {
  const errors: SegmentValidationError[] = [];
  const getPropertyName = (key: string) => propertyMap?.get(key) ?? key;

  const validRules = rules.filter(
    (r) => r.propertyKey.trim() && r.value.length > 0 && r.value.some((v) => v.trim())
  );

  const includeOps = ['=', 'IN'];
  const excludeOps = ['!=', 'NOT IN'];

  const byProperty = new Map<string, RuleDraft[]>();
  for (const r of validRules) {
    const key = r.propertyKey.trim();
    if (!byProperty.has(key)) byProperty.set(key, []);
    byProperty.get(key)!.push(r);
  }

  for (const [, propRules] of byProperty) {
    if (propRules.length < 2) continue;

    const propName = getPropertyName(propRules[0].propertyKey.trim());

    const includeRules = propRules.filter((r) => includeOps.includes(r.operator));
    const excludeRules = propRules.filter((r) => excludeOps.includes(r.operator));

    for (const inc of includeRules) {
      for (const exc of excludeRules) {
        if (arraysOverlap(inc.value, exc.value)) {
          const overlap = inc.value.filter((v) =>
            exc.value.some((ev) => normalizeValue(ev) === normalizeValue(v))
          );
          const displayValues = overlap.slice(0, 3).join(', ');
          const more =
            overlap.length > 3
              ? i18n.t('validation.segmentConflictMore', { count: overlap.length - 3 })
              : '';
          errors.push({
            type: 'direct',
            message: i18n.t('validation.segmentConflict', {
              propName,
              displayValues,
              more
            }),
            ruleIds: [inc.id, exc.id],
          });
        }
      }
    }

    const includeOnly = propRules.filter((r) => includeOps.includes(r.operator));
    if (includeOnly.length >= 2) {
      for (let i = 0; i < includeOnly.length; i++) {
        for (let j = i + 1; j < includeOnly.length; j++) {
          const a = includeOnly[i];
          const b = includeOnly[j];
          if (arraysDisjoint(a.value, b.value)) {
            errors.push({
              type: 'mutually_exclusive',
              message: i18n.t('validation.segmentMutuallyExclusive', {
                propName,
                valuesA: a.value.join(', '),
                valuesB: b.value.join(', ')
              }),
              ruleIds: [a.id, b.id],
            });
          }
        }
      }
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}
